import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';

export type DsButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'link';
export type DsButtonSize = 'sm' | 'md' | 'lg';
export type DsButtonType = 'button' | 'submit' | 'reset';

@Component({
  selector: 'ds-button',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet],
  template: `
    <ng-template #inner><ng-content></ng-content></ng-template>
    @if (href(); as h) {
      <a
        [href]="h"
        [attr.target]="target() ?? null"
        [attr.rel]="target() === '_blank' ? 'noopener noreferrer' : null"
        [class]="classes()"
      >
        <ng-container [ngTemplateOutlet]="inner"></ng-container>
      </a>
    } @else {
      <button
        [type]="type()"
        [disabled]="disabled()"
        [class]="classes()"
      >
        <ng-container [ngTemplateOutlet]="inner"></ng-container>
      </button>
    }
  `,
  styles: [`
    :host {
      display: contents;
    }

    a, button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      border-radius: var(--r-sm);
      font-family: var(--f-body);
      font-weight: 600;
      letter-spacing: 0.005em;
      cursor: pointer;
      white-space: nowrap;
      border: none;
      text-decoration: none;
      transition:
        transform var(--d-2) var(--e-decel),
        box-shadow var(--d-2) var(--e-decel),
        background-color var(--d-2) var(--e-standard),
        color var(--d-2) var(--e-standard);

      &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }

      &:focus-visible {
        outline: 2px solid var(--c-focus);
        outline-offset: 2px;
      }
    }

    /* ── Sizes ────────────────────────────────────────────────── */
    .btn-sm {
      padding: 8px 14px;
      font-size: 13px;
      min-height: 36px;
      gap: 6px;
    }

    .btn-md {
      padding: 12px 20px;
      font-size: 14px;
      min-height: 44px;
      gap: 8px;
    }

    .btn-lg {
      padding: 16px 28px;
      font-size: 16px;
      min-height: 52px;
      gap: 10px;
    }

    /* ── full width ───────────────────────────────────────────── */
    .btn-full {
      width: 100%;
    }

    /* ── Variants ─────────────────────────────────────────────── */
    .btn-primary {
      background-color: var(--c-accent);
      color: var(--c-accent-ink);

      &:hover:not(:disabled) {
        background-color: var(--c-accent-hover);
        transform: translateY(-1px);
        box-shadow: var(--sh-2);
      }

      &:active:not(:disabled) {
        transform: translateY(0);
        box-shadow: none;
        transition-duration: 80ms;
      }
    }

    .btn-secondary {
      background-color: var(--on-surface);
      color: var(--surface);

      &:hover:not(:disabled) {
        background-color: var(--c-n-800);
        transform: translateY(-1px);
        box-shadow: var(--sh-2);
      }

      &:active:not(:disabled) {
        transform: translateY(0);
        box-shadow: none;
        transition-duration: 80ms;
      }
    }

    .btn-tertiary {
      background-color: transparent;
      color: var(--on-surface);
      border: 1.5px solid var(--on-surface);

      &:hover:not(:disabled) {
        background-color: rgba(0, 0, 0, 0.04);
        transform: translateY(-1px);
        box-shadow: var(--sh-2);
      }

      &:active:not(:disabled) {
        transform: translateY(0);
        box-shadow: none;
        transition-duration: 80ms;
      }

      :where(.dark) & {
        &:hover:not(:disabled) {
          background-color: rgba(255, 255, 255, 0.06);
        }
      }
    }

    .btn-ghost {
      background-color: transparent;
      color: var(--on-surface);

      &:hover:not(:disabled) {
        background-color: rgba(0, 0, 0, 0.06);
        transform: translateY(-1px);
        box-shadow: var(--sh-2);
      }

      &:active:not(:disabled) {
        transform: translateY(0);
        box-shadow: none;
        transition-duration: 80ms;
      }

      :where(.dark) & {
        &:hover:not(:disabled) {
          background-color: rgba(255, 255, 255, 0.06);
        }
      }
    }

    .btn-link {
      background-color: transparent;
      color: #CC2831;
      text-decoration: underline;
      border: none;
      min-height: unset;
      padding: 0;
      border-radius: 2px;

      &:hover:not(:disabled) {
        color: var(--c-accent);
        transform: none;
        box-shadow: none;
      }

      &:focus-visible {
        outline-offset: 4px;
      }
    }
  `],
})
export class DsButtonComponent {
  readonly variant = input<DsButtonVariant>('primary');
  readonly size = input<DsButtonSize>('md');
  readonly disabled = input<boolean>(false);
  readonly fullWidth = input<boolean>(false);
  readonly type = input<DsButtonType>('button');
  readonly href = input<string | null>(null);
  readonly target = input<'_blank' | '_self' | null>(null);

  readonly classes = computed(() => {
    const parts = [
      `btn-${this.variant()}`,
      `btn-${this.size()}`,
    ];
    if (this.fullWidth()) parts.push('btn-full');
    return parts.join(' ');
  });
}
